import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSettingsContext, useUserSettingsContext } from 'contexts';
import { getLanguage, HOT_KEY, useHotKey } from '@web-chat-core/ui-kit';
import i18next from '@web-chat-core/ui-kit/lib/i18n';
import { VirtualBackgroundButton } from 'features/VirtualBackground/components/VirtualBackgroundModalButton/VirtualBackgroundButton';
import { BackgroundTransformer } from 'features/VirtualBackground/source';
import { getAssetPaths } from 'features/VirtualBackground/utils/assetPaths';
import { AspectRatioStyled } from 'features/PreJoin/components/JoinPanel/view';
import { useManageVideoDevices } from 'features/PreJoin/hooks/useManageVideoDevices';
import { useWebinarPrejoinMediaLock } from 'useCase/webinar/useWebinarPrejoinMediaLock';
import { selectLocalVideoTrack } from 'state/selectors/localTracks';
import { AudioButton, VideoButton } from 'ui/components';
import { useKeyPress } from 'ui/hooks/useKeyPress';
import { ModalName } from 'features/Modals';
import { useModal } from 'useCase/modal/useModal';
import { isFeatureOn } from 'useCase/featureFlags/featureFlagsApi';
import { useRoomInfo } from 'useCase/room/useRoomInfo';
import { observer } from 'mobx-react-lite';
import { selectIsModalOpened } from 'state/selectors';
import { useCanSeeCallSettings } from 'features/CallSettings/hooks/useCanSeeCallSettings';
import { BASE_ASPECT_RATIO, FEATURES, LANGUAGE_STORAGE_KEY } from '../../consts';
import { OptionsContainer, Sidebar, SidebarContent } from '../view';
import { Header } from '../../ui/components/Header/Header';
import { AdditionalMenu } from '../../features/Room/components/AdditionalMenu/AdditionalMenu';
import {
    ControlElements,
    ControlSection,
    EmptyNameError,
    NameField,
    NameFieldContainer,
    NameFieldLabel,
    NameFieldSection,
    NameWithControlsWrapper,
    PreJoinContainer,
    PreJoinPageContent,
    VideoContainer,
    VideoPlaceholder,
    VideoRenderer,
} from './view';

if (BackgroundTransformer.isSupported) {
    BackgroundTransformer.preload(getAssetPaths().tasksVisionFileSet).catch(() => {});
}

interface Props {
    userName: string;
    embeddedMode?: boolean;
    isNameReadOnly?: boolean;
    isEmptyNameError?: boolean;
    isParticipantsLimitError?: boolean;
    isParticipantsLimitRetryLoading?: boolean;
    children?: React.ReactNode;
    onChange?: (data: { name: string }) => void;
    onParticipantsLimitRetry?: () => void;
    onParticipantsLimitClose?: () => void;
}

export const PreJoinPage = observer(
    ({
        embeddedMode = false,
        isNameReadOnly = false,
        isEmptyNameError = false,
        isParticipantsLimitError = false,
        isParticipantsLimitRetryLoading = false,
        userName,
        children,
        onChange,
        onParticipantsLimitRetry = () => undefined,
        onParticipantsLimitClose = () => undefined,
    }: Props) => {
        const { t } = useTranslation();
        const location = useLocation();

        const { audioEnabled, setAudioEnabled, setAudioInputDeviceId, setAudioOutputDeviceId } =
            useUserSettingsContext();
        const { simulcast, setSimulcast, dynacast, setDynacast, adaptiveStream, setAdaptiveStream } =
            useSettingsContext();
        const { toggleVideo, selectVideoDevice } = useManageVideoDevices();
        const { isMicLocked, isCamLocked } = useWebinarPrejoinMediaLock();
        const { toggleModal, openModal, closeModal } = useModal();
        const { setIsPreJoin } = useRoomInfo();
        const localVideoTrack = selectLocalVideoTrack();

        const lang = localStorage.getItem(LANGUAGE_STORAGE_KEY) || getLanguage();

        const [language, setLanguage] = useState<string>(lang);
        const [isOpen, setOpen] = useState(false);

        const pressCallback = useCallback(
            (evt: React.SyntheticEvent) => {
                evt.preventDefault();
                setOpen(!isOpen);
            },
            [isOpen],
        );

        useKeyPress(['o'], pressCallback);

        const setPersistLanguage = useCallback((language: string) => {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
            setLanguage(language);
        }, []);

        useEffect(() => {
            i18next.changeLanguage(lang);
        }, [lang]);

        const toggleAudio = useCallback(() => {
            if (isMicLocked) return;
            setAudioEnabled(!audioEnabled);
        }, [audioEnabled, isMicLocked, setAudioEnabled]);

        const handleToggleVideo = useCallback(() => {
            if (isCamLocked) return;
            toggleVideo();
        }, [isCamLocked, toggleVideo]);

        const selectAudioInputDevice = useCallback(({ deviceId }: MediaDeviceInfo) => {
            setAudioInputDeviceId(deviceId);
        }, []);

        const selectAudioOutputDevice = useCallback(({ deviceId }: MediaDeviceInfo) => {
            setAudioOutputDeviceId(deviceId);
        }, []);

        const onChangeSimulcast = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => setSimulcast(e.target.checked),
            [],
        );

        const onChangeDynacast = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => setDynacast(e.target.checked),
            [],
        );

        const onChangeAdaptiveStream = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => setAdaptiveStream(e.target.checked),
            [],
        );

        const onChangeUserName = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                onChange?.({ name: e.target.value });
            },
            [onChange],
        );

        const isCanSeeCallSettings = useCanSeeCallSettings();

        useHotKey(HOT_KEY.TOGGLE_MICRO, toggleAudio, { handlerName: 'toggle-micro' }, [audioEnabled, isMicLocked]);
        useHotKey(HOT_KEY.TOGGLE_CAMERA, handleToggleVideo, { handlerName: 'toggle-camera' }, [handleToggleVideo]);

        useEffect(() => {
            // Используем location.search из react-router-dom, который работает и для HashRouter, и для BrowserRouter
            const urlParams = new URLSearchParams(location.search);
            const isKicked = urlParams.get('kicked') === '1';

            if (isKicked) {
                toggleModal(ModalName.KICKED);
            }
        }, [location.search]);

        useEffect(() => {
            if (isParticipantsLimitError) {
                openModal(ModalName.PARTICIPANTS_LIMIT, {
                    isRetryLoading: isParticipantsLimitRetryLoading,
                    onRetry: onParticipantsLimitRetry,
                    onClose: onParticipantsLimitClose,
                });
                return;
            }

            if (selectIsModalOpened(ModalName.PARTICIPANTS_LIMIT)) {
                closeModal();
            }
        }, [
            isParticipantsLimitError,
            isParticipantsLimitRetryLoading,
            onParticipantsLimitRetry,
            onParticipantsLimitClose,
        ]);

        useEffect(() => {
            setIsPreJoin(true);
            return () => {
                setIsPreJoin(false);
            };
        }, []);

        let videoElement: ReactElement;
        if (localVideoTrack) {
            videoElement = <VideoRenderer track={localVideoTrack} isLocal={true} />;
        } else {
            videoElement = <VideoPlaceholder>{t('web_call_camera_off')}</VideoPlaceholder>;
        }

        const isPushToTalkEnabled = isFeatureOn(FEATURES.VCS_MIC_PUSH_ENABLED);
        const isMicToggleEnabled = isFeatureOn(FEATURES.VCS_MIC_TOGGLE_ENABLED);
        const isAdvancedSoundAndCameraSettingsEnabled = isFeatureOn(
            FEATURES.VCS_ADVANCED_SOUND_CAMERA_SETTINGS_ENABLED,
        );

        return (
            <PreJoinContainer id="modal-root">
                <Sidebar isOpen={isOpen}>
                    <SidebarContent>
                        <OptionsContainer>
                            <div>
                                <input
                                    id="simulcast-option"
                                    type="checkbox"
                                    name="simulcast"
                                    checked={simulcast}
                                    onChange={onChangeSimulcast}
                                />
                                <label htmlFor="simulcast-option">Simulcast</label>
                            </div>
                            <div>
                                <input
                                    id="dynacast-option"
                                    type="checkbox"
                                    name="dynacast"
                                    checked={dynacast}
                                    onChange={onChangeDynacast}
                                />
                                <label htmlFor="dynacast-option">Dynacast</label>
                            </div>
                            <div>
                                <input
                                    id="adaptivestream-option"
                                    type="checkbox"
                                    name="adaptiveStream"
                                    checked={adaptiveStream}
                                    onChange={onChangeAdaptiveStream}
                                />
                                <label htmlFor="adaptivestream-option">Adaptive Stream</label>
                            </div>
                        </OptionsContainer>
                    </SidebarContent>
                </Sidebar>
                <main>
                    <Header language={language} embeddedMode={embeddedMode} setPersistLanguage={setPersistLanguage} />

                    <PreJoinPageContent>
                        <VideoContainer $isNoVideoTrack={!localVideoTrack}>
                            <AspectRatioStyled ratio={BASE_ASPECT_RATIO}>{videoElement}</AspectRatioStyled>
                        </VideoContainer>

                        <NameWithControlsWrapper>
                            {!embeddedMode && (
                                <NameFieldSection>
                                    <NameFieldLabel htmlFor="token">{t('web_call_name')}</NameFieldLabel>
                                    <NameFieldContainer
                                        $hasError={isEmptyNameError}
                                        data-tooltip={t('web_call_cannot_empty')}
                                    >
                                        <NameField
                                            type="text"
                                            name="token"
                                            value={userName}
                                            onChange={onChangeUserName}
                                            readOnly={isNameReadOnly}
                                            maxLength={80}
                                            autoFocus
                                        />
                                    </NameFieldContainer>
                                    <EmptyNameError
                                        variant="caption2R"
                                        colorVariant="danger"
                                        $isVisible={isEmptyNameError}
                                    >
                                        {t('vks_videoconference_required_field')}
                                    </EmptyNameError>
                                </NameFieldSection>
                            )}

                            <ControlSection>
                                <ControlElements>
                                    <AudioButton
                                        isPreJoinPage
                                        isPushToTalkEnabled={isPushToTalkEnabled}
                                        isToggleMicEnabled={isMicToggleEnabled}
                                        isAdvancedSoundAndCameraSettingsEnabled={
                                            isAdvancedSoundAndCameraSettingsEnabled
                                        }
                                        isButtonDisabled={isMicLocked}
                                        onClick={toggleAudio}
                                        onAudioInputSourceSelected={selectAudioInputDevice}
                                        onAudioOutputSourceSelected={selectAudioOutputDevice}
                                    />
                                    <VideoButton
                                        onClick={handleToggleVideo}
                                        onSourceSelected={selectVideoDevice}
                                        isButtonDisabled={isCamLocked}
                                        isAdvancedSoundAndCameraSettingsEnabled={
                                            isAdvancedSoundAndCameraSettingsEnabled
                                        }
                                    />
                                    {isCanSeeCallSettings ? (
                                        <AdditionalMenu placement="top" />
                                    ) : (
                                        <VirtualBackgroundButton />
                                    )}
                                </ControlElements>
                                {children}
                            </ControlSection>
                        </NameWithControlsWrapper>
                    </PreJoinPageContent>
                </main>
            </PreJoinContainer>
        );
    },
);
